import { useEffect, useState } from 'react';
import { useLinkedInTokenStatus } from '@/hooks';
import { useToast } from '@/contexts/ToastContext';
import { useUser } from '@/hooks';

/**
 * Hook to manage LinkedIn integration state and OAuth callbacks
 */
export const useLinkedInIntegration = () => {
  const user = useUser();
  const { addToast } = useToast();
  const {
    hasToken: hasLinkedInToken,
    isLoading: tokenStatusLoading,
    refetch: refetchTokenStatus,
  } = useLinkedInTokenStatus();

  const [showLinkedInModal, setShowLinkedInModal] = useState(false);
  const [isLinkedInModalClosable, setIsLinkedInModalClosable] = useState(true);

  // Auto-show LinkedIn modal for external users without token
  useEffect(() => {
    // Only for external users
    if (user?.role !== 'external') return;

    // Wait for token status to finish loading
    if (tokenStatusLoading) return;

    // Show modal if user doesn't have LinkedIn token
    if (!hasLinkedInToken) {
      setShowLinkedInModal(true);
      setIsLinkedInModalClosable(false); // Non-closable when auto-shown
    } else {
      // If user has token, ensure modal is closed
      setShowLinkedInModal(false);
    }
  }, [user?.role, tokenStatusLoading, hasLinkedInToken]);

  // Handle OAuth callback from URL params
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const success = urlParams.get('linkedin_posting_success');
    const error = urlParams.get('linkedin_posting_error');

    if (success === 'true') {
      // Remove query params from URL to clean up the address bar
      window.history.replaceState({}, document.title, '/marketing');

      // Refetch token status
      refetchTokenStatus();

      // Auto-close the modal after successful connection
      setShowLinkedInModal(false);
      setIsLinkedInModalClosable(true); // Reset to closable for future use

      // Show success toast notification
      addToast('LinkedIn account connected successfully! You can now post to LinkedIn.', 'success');
    } else if (error) {
      // Remove query params from URL to clean up the address bar
      window.history.replaceState({}, document.title, '/marketing');

      // Show error toast notification
      addToast(`Failed to connect LinkedIn: ${decodeURIComponent(error)}`, 'error');
    }
  }, [refetchTokenStatus, addToast]);

  const handleLinkedInConnect = () => {
    setShowLinkedInModal(false);
    // The modal will handle the OAuth redirect
  };

  const openLinkedInModal = (closable: boolean = true) => {
    setShowLinkedInModal(true);
    setIsLinkedInModalClosable(closable);
  };

  const closeLinkedInModal = () => {
    setShowLinkedInModal(false);
    setIsLinkedInModalClosable(true);
  };

  return {
    hasLinkedInToken,
    tokenStatusLoading,
    showLinkedInModal,
    isLinkedInModalClosable,
    openLinkedInModal,
    closeLinkedInModal,
    handleLinkedInConnect,
  };
};

